/**
 * Всё, что жест переноса делает с НАСТОЯЩИМ DOM, а не со своим состоянием:
 * клетка под указателем и съеденное нажатие.
 *
 * Своим файлом с 12.09.2026: `use-calendar-drag.ts` упёрся в потолок в 400
 * строк (раздел 9 CLAUDE.md), когда жест отдали пальцу. Граница разреза
 * проходит по проверяемости: здесь живёт то, чего в `pnpm test` не проверить
 * вовсе (DOM нужен настоящий), а в хуке остаётся состояние жеста. Что при этом
 * СЧИТАЕТСЯ, лежит в `entry-drag.ts` и `drag-edge.ts` чистыми функциями с
 * тестами.
 *
 * Проект целиком в `docs/schedule-drag-design.md`.
 */

/** Сколько минут в одной клетке фона. Та же величина, которой нарезана колонка. */
const SLOT_MIN = 30;

/**
 * Клетка фона под точкой.
 *
 * ⚠️ **Ищется в СПИСКЕ элементов под точкой, а не в верхнем** (12.09.2026).
 * Коробки занятий перестают ловить указатель правилом `.cal[data-dragging]`, но
 * правило приезжает следующим кадром React, а призрак рисуется уже в момент
 * взятия: у пальца курсора нет, и картинка это единственный ответ на вопрос
 * «взялось ли». В эту секунду верхним элементом под пальцем оказывается сама
 * коробка, и `elementFromPoint` вернул бы её, то есть призрака не было бы. Для
 * мыши результат тот же самый, поэтому ветки по типу указателя тут нет.
 */
function slotAt(x: number, y: number): HTMLElement | null {
  for (const el of document.elementsFromPoint(x, y)) {
    const slot = el.closest<HTMLElement>(".cal__slot");
    if (slot !== null) return slot;
  }
  return null;
}

/**
 * Минута под указателем: дата колонки плюс время, посчитанное ВНУТРИ клетки.
 *
 * ⚠️ **Дату и получас даёт разметка** (`data-date`, `data-min` на получасовой
 * кнопке фона), а пиксели добавляют только положение внутри этого получаса. Так
 * шаг в пять минут стал возможен, но день и час не выводятся из координат:
 * ошибка в пикселях стоит минут, а не суток.
 *
 * Высота клетки берётся у неё самой, а не из переменной стилей: `--cal-hour`
 * задан в `rem`, и при другом кегле шрифта расчёт по константе разъехался бы с
 * тем, что человек видит.
 */
export function minuteAt(x: number, y: number): { date: string; min: number } | null {
  const slot = slotAt(x, y);
  const date = slot?.dataset.date;
  const min = slot?.dataset.min;
  if (slot === null || date === undefined || min === undefined) return null;
  const box = slot.getBoundingClientRect();
  const inside = box.height <= 0 ? 0 : ((y - box.top) / box.height) * SLOT_MIN;
  return { date, min: Number(min) + inside };
}

/**
 * Съесть ОДНО нажатие после переноса.
 *
 * ⚠️ Без этого отпускание над коробкой занятия открывало бы карточку сразу
 * после переезда: `click` приходит после `pointerup` и ничего не знает о том,
 * что между ними был жест.
 *
 * ⚠️ **Зовётся только если указатель ПРОЕХАЛ** (12.09.2026). Палец, который
 * подержали и отпустили на месте, переносить некуда, и человек обязан получить
 * ровно то, чего хотел нажатием, то есть карточку.
 */
export function eatClick() {
  const eat = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    window.removeEventListener("click", eat, true);
  };
  window.addEventListener("click", eat, true);
  // Нажатие приходит не всегда (отпустили над другим элементом), и вечно
  // висящий перехватчик съел бы следующее настоящее
  setTimeout(() => {
    window.removeEventListener("click", eat, true);
  }, 0);
}
